import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Select, TextInput, Checkbox } from 'components/Form'
import { createApiRecord, updateApiRecord } from 'services/MachshipIntegrationService'
import { useDispatch, useSelector } from 'react-redux'
import { getRecord } from './store/dataSlice'
import reducer from './store'
import { injectReducer } from 'store'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { Success } from 'utils/alerts'

injectReducer('machshipIntegration', reducer)

const validationSchema = Yup.object().shape({
    company: Yup.string().required('Company field is required'),
    api_url: Yup.string().url('Must be a valid URL').required('API URL field is required'),
    api_token: Yup.string().required('API Token field is required'),
})

const Create = () => {
    const { id } = useParams()
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const { companyData } = useSelector(state => state.listData)
    const { view, record, loading } = useSelector(state => state.machshipIntegration.data)

    useEffect(() => {
        if (id) {
            fetchData(id)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    const fetchData = id => dispatch(getRecord(id))

    const handleDiscard = () => navigate('/company-management/machship-integration')

    const handleSubmit = data => {
        if (id) {
            updateApiRecord(id, data)
                .then(() => {
                    Success('Updated Successfully!')
                    handleDiscard()
                })
                .catch(error => console.log('Error: ', error))
        } else {
            createApiRecord(data)
                .then(response => {
                    Success('Created Successfully!')
                    handleDiscard()
                })
                .catch(error => console.log('Error: ', error))
        }
    }

    if (loading) {
        return null
    }

    return (
        <>
            <div className="flex flex-col gap-1 mb-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">Machship Integration</h2>
            </div>
            <div className="flex flex-col items-center">
                <div className="w-full bg-white border rounded-sm border-stroke shadow-default dark:border-strokedark dark:bg-boxdark">
                    <div className="px-5 py-3 border-b border-stroke dark:border-strokedark">
                        <h3 className="font-medium text-black dark:text-white">
                            {id ? 'Update Machship Integration' : 'Add New Machship Integration'}
                        </h3>
                    </div>
                    <div className="flex flex-col gap-3 p-6">
                        <Formik
                            initialValues={id ? record : view}
                            validationSchema={validationSchema}
                            onSubmit={values => {
                                handleSubmit(values)
                            }}
                        >
                            {({ values, touched, errors }) => (
                                <Form>
                                    <div className="grid grid-cols-1 gap-x-4 gap-y-4 md:grid-cols-4">
                                        <Field name="company">
                                            {({ field, form }) => (
                                                <Select
                                                    id="company"
                                                    name="company"
                                                    label="Company"
                                                    placeholder="Select Company"
                                                    className="md:col-span-2"
                                                    value={values?.company}
                                                    options={companyData}
                                                    onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                    error={touched.company && errors.company}
                                                    message={errors.company}
                                                />
                                            )}
                                        </Field>
                                        {/* <Field name="customer">
                                            {({ field, form }) => (
                                                <Select
                                                    id="customer"
                                                    name="customer"
                                                    label="Customer (Optional)"
                                                    placeholder="Select Customer (Optional)"
                                                    className="md:col-span-2"
                                                    value={values?.customer}
                                                    options={customersData}
                                                    onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                    error={touched.customer && errors.customer}
                                                    message={errors.customer}
                                                />
                                            )}
                                        </Field> */}
                                        <Field name="api_url">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="API URL"
                                                    name="api_url"
                                                    placeholder="https://live.machship.com/apiv2"
                                                    error={errors.api_url && touched.api_url}
                                                    message={errors.api_url}
                                                    value={values?.api_url}
                                                    onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                />
                                            )}
                                        </Field>
                                        <Field name="api_token">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="password"
                                                    className="md:col-span-2"
                                                    label="API Token"
                                                    name="api_token"
                                                    placeholder="Enter your Machship API token"
                                                    error={errors.api_token && touched.api_token}
                                                    message={errors.api_token}
                                                    value={values?.api_token}
                                                    onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                />
                                            )}
                                        </Field>
                                        <Field name="is_test_mode">
                                            {({ field, form }) => (
                                                <div className="md:col-span-2">
                                                    <Checkbox
                                                        id="is_test_mode"
                                                        name="is_test_mode"
                                                        checked={values?.is_test_mode || false}
                                                        onChange={e => form.setFieldValue(field.name, e.target.checked)}
                                                        label="Test Mode"
                                                    />
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        Enable test mode for development and testing
                                                    </p>
                                                </div>
                                            )}
                                        </Field>
                                        <Field name="is_active">
                                            {({ field, form }) => (
                                                <div className="md:col-span-2">
                                                    <Checkbox
                                                        id="is_active"
                                                        name="is_active"
                                                        checked={values?.is_active !== false}
                                                        onChange={e => form.setFieldValue(field.name, e.target.checked)}
                                                        label="Active Status"
                                                    />
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        Enable or disable this integration
                                                    </p>
                                                </div>
                                            )}
                                        </Field>
                                    </div>
                                    <div className="flex items-center justify-end gap-4 mt-6">
                                        <Button
                                            className="h-10 w-18 text-primary border border-primary dark:text-secondary dark:border-secondary"
                                            onClick={handleDiscard}
                                        >
                                            Discard
                                        </Button>
                                        <Button
                                            type="submit"
                                            className="flex items-center justify-center px-6 py-2 text-white transition border rounded-lg cursor-pointer hover:bg-opacity-90 border-primary bg-primary dark:border-secondary dark:bg-secondary"
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Create
